import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  CallControls,
  PaxityProgress,
  SideMenu,
} from '../../components';
import ExplainOverlay from '../../components/ExplainOverlay';
import {
  useCases,
  useAuth,
  useTownAssets,
  useDisplay,
} from '../../stores';
import {
  appLinks,
  casesStatusParam,
  casesTeamsParam,
} from '../../utils';

// Page chunks are lazy so the first paint stays small, then warmed during idle
// time (see Dashboard) so a menu click finds its chunk already in the module cache
const pageLoaders = {
  cases: () => import('../Cases/CasesPage'),
  comm: () => import('../Comm/CommsPage'),
  home: () => import('../Homepage/HomePage'),
  profile: () => import('../Profile/ProfilePage'),
  qrs: () => import('../Qrs/QrsPage'),
  settings: () => import('../Settings/SettingsPage'),
  tasks: () => import('../Tasks/TasksPage'),
  responders: () => import('../Teams/TeamsPage'),
  polls: () => import('../Polls/PollsPage'),
  users: () => import('../Users/UsersPage'),
  partners: () => import('../Partners/PartnersPage'),
  guide: () => import('../Help/GuidePage'),
};
const CasesPage = lazy(pageLoaders.cases);
const CommsPage = lazy(pageLoaders.comm);
const HomePage = lazy(pageLoaders.home);
const ProfilePage = lazy(pageLoaders.profile);
const QrsPage = lazy(pageLoaders.qrs);
const SettingsPage = lazy(pageLoaders.settings);
const TasksPage = lazy(pageLoaders.tasks);
const TeamsPage = lazy(pageLoaders.responders);
const PollsPage = lazy(pageLoaders.polls);
const UsersPage = lazy(pageLoaders.users);
const PartnersPage = lazy(pageLoaders.partners);
const GuidePage = lazy(pageLoaders.guide);

function Dashboard() {
  const { cases } = useCases();
  const { assets } = useTownAssets();
  const { auth } = useAuth();
  const { info: authInfo } = auth;
  const { language, loadLanguage } = assets;
  const {
    refresh,
    removedCsid,
    removedCsidSeq,
  } = cases;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The selected case is held in the URL (?cs=<csid>&i=<piid>) so it is
  // shareable, survives reloads, and can be opened from elsewhere (e.g. a QR
  // code's case list). Clicking a case writes the URL; the URL drives display.
  const csid = searchParams.get('cs');
  const iParam = searchParams.get('i');
  const casePiid = (iParam && (iParam !== 'home')) ? iParam : undefined;
  const caseSelected = useMemo(
    () => ((csid)
      ? {
          csid,
          piid: casePiid,
        }
      : null),
    [csid, casePiid],
  );
  // Carries the one-off filter overrides over: the link builder rewrites the
  // whole query, so omitting them here would silently drop them on every case click
  const teamsOverride = searchParams.get(casesTeamsParam);
  const statusesOverride = searchParams.get(casesStatusParam);
  const setCaseSelected = useCallback(
    (sel) => navigate(appLinks.casesListCase({
      csid: sel?.csid,
      piid: sel?.piid,
      gpids: teamsOverride?.split(','),
      statuses: statusesOverride?.split(','),
    })),
    [navigate, teamsOverride, statusesOverride],
  );

  // When a case is dropped from the list because access was lost, deselect it
  // if it is the one open, so its chat/info stops acting on an unreadable case.
  // Keyed on removedCsidSeq so each removal is handled exactly once.
  const handledRemovalRef = useRef(0);
  useEffect(
    () => {
      if (removedCsidSeq === handledRemovalRef.current) return;
      handledRemovalRef.current = removedCsidSeq;
      if (removedCsid && (caseSelected?.csid === removedCsid)) {
        // Use void to make it clear we do not want to wait this async function:
        void setCaseSelected(null);
      }
    },
    [removedCsidSeq, removedCsid, caseSelected, setCaseSelected],
  );

  const { display, mainMenu } = useDisplay();
  const { overlay } = display;
  const { navigatingTo, setNavigatingTo } = mainMenu;

  // The zoomed shell is sized a sub-pixel larger than the window to avoid a white edge seam; clip that
  // overshoot here. #root only needs to scroll when the shell can't fit the window even at this zoom
  // (tiny window, or the old-Firefox no-zoom path). Restored to the stylesheet default when leaving.
  useEffect(
    () => {
      const rootEl = document.getElementById('root');
      if (!rootEl) return undefined;
      rootEl.style.overflow = display.shellFitsWindow ? 'hidden' : 'auto';
      return () => {
        rootEl.style.overflow = '';
      };
    },
    [display.shellFitsWindow],
  );

  // Warm every page chunk once the landing page is up; a chunk already fetched here
  // resolves from the module cache when its lazy() is first rendered
  useEffect(
    () => {
      const warm = () => {
        Object.values(pageLoaders).forEach((load) => {
          load().catch(() => {});
        });
      };
      if (typeof window.requestIdleCallback === 'function') {
        const handle = window.requestIdleCallback(warm);
        return () => window.cancelIdleCallback(handle);
      }
      const timer = setTimeout(warm, 1000);
      return () => clearTimeout(timer);
    },
    [],
  );

  const location = useLocation();
  const currentPage = location.pathname.split('/')[1];

  // Clear navigation state when we arrive at the destination
  useEffect(
    () => {
      if (navigatingTo && (currentPage === navigatingTo)) {
        setNavigatingTo(null);
      }
    },
    [currentPage, navigatingTo, setNavigatingTo],
  );

  const isNavigating = Boolean(navigatingTo);

  const numbers = [];
  for (let i = 0; i < 256; i += 1) {
    numbers.push(i);
  }

  useEffect(
    () => {
      if (authInfo
        && authInfo.settings?.user?.language
        && language?.language
        && (authInfo.settings.user.language !== language.language)
      ) {
        // console.log(`Current language=${
        //   language.language}, loading ${authInfo.settings.user.language}`);
        loadLanguage(authInfo.settings.user.language);
      }
    },
    [authInfo, language, loadLanguage],
  );

  useEffect(
    () => {
      if (refresh) {
        // console.log('Setup');
        // auto-refresh
        refresh(true);

        return (
          () => {
            // console.log('Cleanup');
            refresh(false);
          });
      }
      return null;
    },
    [refresh],
  );

  return (
    // h-full/w-full (not screen units) so .root scrollbars are accounted for. The shell's min box
    // (which makes .root scroll on small viewports) comes from display.shellZoomStyle, keeping the
    // minimum layout size defined once in DisplayProvider.
    <main
      className="h-full w-full absolute l-0 overflow-hidden flex flex-row items-stretch font-light text-start text-base"
      style={display.shellZoomStyle}
    >

      <SideMenu />

      <div className="flex-1 relative overflow-hidden">
        {/* Loading overlay during navigation */}
        {isNavigating && (
          <div className="absolute inset-0 bg-black/30 z-50 flex items-center justify-center">
            <PaxityProgress />
          </div>
        )}

        <Suspense fallback={<PaxityProgress withDiv />}>
          <Routes>
            <Route
              path="/home/*"
              element={<HomePage setCaseSelected={setCaseSelected} />}
            />

            <Route
              path="/cases/*"
              element={(
                <CasesPage
                  caseSelected={caseSelected}
                  setCaseSelected={setCaseSelected}
                />
              )}
            />

            <Route
              path="/history"
              element={(
                <h3>
                  test
                </h3>
              )}
            />

            <Route
              path="/users/*"
              element={<UsersPage />}
            />

            <Route
              path="/settings/*"
              element={<SettingsPage />}
            />

            <Route
              path="/responders/*"
              element={<TeamsPage />}
            />

            <Route
              path="/partners/*"
              element={<PartnersPage />}
            />

            <Route
              path="/tasks/*"
              element={<TasksPage />}
            />

            <Route
              path="/qrs/*"
              element={<QrsPage />}
            />

            <Route
              path="/polls/*"
              element={<PollsPage />}
            />

            <Route
              path="/comm/*"
              element={<CommsPage />}
            />

            {/* Bookmarks from when statistics were their own section */}
            <Route
              path="/stats/*"
              element={(
                <Navigate
                  to={{
                    pathname: appLinks.casesStats(),
                    search: location.search,
                  }}
                  replace
                />
              )}
            />

            {/* <Route
              path="/logs"
              element={<Logs />}
            /> */}

            <Route
              path="/profile/*"
              element={<ProfilePage />}
            />

            <Route
              path="/guide/*"
              element={<GuidePage />}
            />

            <Route
              path="/demo"
              element="demo"
            />

            <Route
              path="*"
              element={(
                <div style={{ color: 'white' }}>
                  Invalid path, redirecting...
                  <Navigate to="/home" replace />
                </div>
              )}
            />
          </Routes>
        </Suspense>
      </div>

      <CallControls setCaseSelected={setCaseSelected} />

      <ExplainOverlay />

      {overlay || null}
    </main>
  );
}

export default Dashboard;
